import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Users, TrendingUp } from "lucide-react";
import { useChat } from "@/context/ChatContext";

const EASE = [0.22, 1, 0.36, 1];

function MaskLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { openChat } = useChat();
  const { scrollY } = useScroll();
  const yMain = useTransform(scrollY, [0, 600], [0, 70]);
  const ySecond = useTransform(scrollY, [0, 600], [0, -40]);

  return (
    <section id="topo" className="relative overflow-hidden bg-white pt-32 pb-28 sm:pt-36 sm:pb-16 lg:pt-40 lg:pb-24">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-brand-50 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-brand-100/60 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600"
          >
            Consultora Porto Seguro
          </motion.p>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.1}>Conquiste seu imóvel</MaskLine>
            <MaskLine delay={0.22}>ou seu veículo</MaskLine>
            <MaskLine delay={0.34}>
              <span className="text-brand-600">sem juros,</span> com
            </MaskLine>
            <MaskLine delay={0.46}>planejamento</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            O consórcio é o caminho planejado para realizar a compra: sem os juros do financiamento,
            com parcela que caiba no seu bolso e acompanhamento humano do primeiro até o dia da sua carta de crédito.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.72 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-imovel-button"
              onClick={() => openChat("imovel")}
              className="group rounded-full bg-brand-600 px-7 py-4 text-base font-bold text-white shadow-lg shadow-brand-600/20 transition-all hover:-translate-y-0.5 hover:bg-brand-700"
            >
              Quero um imóvel
              <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">→</span>
            </button>
            <button
              data-testid="hero-veiculo-button"
              onClick={() => openChat("veiculo")}
              className="rounded-full border-2 border-brand-600/15 bg-white px-7 py-4 text-base font-bold text-brand-600 transition-all hover:-translate-y-0.5 hover:border-brand-600/40 hover:shadow-lg"
            >
              Quero um veículo
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold text-slate-500"
          >
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-600" /> Taxas transparentes
            </li>
            <li className="flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-600" /> Atendimento humano
            </li>
            <li className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-brand-600" /> Poder de compra à vista
            </li>
          </motion.ul>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-6 max-w-lg text-xs leading-relaxed text-slate-400"
          >
            Simulações e condições sujeitas à análise e às regras do grupo de consórcio vigente.
            Consórcio tem taxa de administração e fundo de reserva — sem promessa de contemplação.
          </motion.p>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            className="relative"
          >
            <motion.div style={{ y: yMain }} className="relative ml-auto w-full max-w-[520px]">
              <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-brand-900/20">
                <img
                  src="https://images.pexels.com/photos/1974596/pexels-photo-1974596.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Fachada de residência moderna ao entardecer"
                  className="h-[300px] w-full object-cover sm:h-[380px] lg:h-[430px]"
                  loading="eager"
                />
              </div>

              <motion.div
                style={{ y: ySecond }}
                className="absolute -bottom-10 -left-2 w-44 overflow-hidden rounded-3xl border-4 border-white shadow-xl sm:-left-8 sm:w-56"
              >
                <img
                  src="https://images.pexels.com/photos/2920064/pexels-photo-2920064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="SUV dirigindo em estrada"
                  className="h-32 w-full object-cover sm:h-40"
                  loading="lazy"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 1.1 }}
                className="glass absolute -top-5 right-2 flex items-center gap-3 rounded-2xl px-4 py-3 sm:right-6"
              >
                <img src="/isabela.jpg" alt="Isabela Guedes" className="h-11 w-11 rounded-full object-cover" />
                <div className="leading-tight">
                  <p className="text-sm font-bold text-slate-900">Isabela Guedes</p>
                  <p className="text-xs font-semibold text-brand-600">Atendimento humano de verdade</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
